import type { RecipientType } from '~/components/communications/CommunicationCard/constants';
import type { ProvidersUnion } from '~/components/communications/VideoCommunication/types';
import type { ClaimCommunicationItemIdsParams } from '~/server_shared/openapi/data-contracts';
import type {
  Category,
  Communication as DataContractsCommunication,
  ContactBaseModel,
  ContactPhone,
  EmailCommunicationBase as DataContractsEmailCommunicationBase,
  PhoneCallCommunication,
} from '~/server_shared/swagger/data-contracts';
import type { COMMUNICATION_CHANNEL_DICT } from '~/Types';

export type CommunicationChannelKey = keyof typeof COMMUNICATION_CHANNEL_DICT;

/** Communication channel types from backend OpenAPI schema */
export type CommunicationChannel = NonNullable<ClaimCommunicationItemIdsParams['communication_channel']>;

export type ChannelTabType = CommunicationChannel | 'all';

/** Handled status filter values from backend OpenAPI schema, plus the unfiltered tab state */
export type HandledStatusFilter = NonNullable<ClaimCommunicationItemIdsParams['handled_status']> | 'all';

const COMMUNICATION_CHANNELS = [
  'email',
  'sms',
  'phone_call',
  'physical_mail',
  'video_call',
  'vendor',
] as const satisfies readonly CommunicationChannel[];

export const COMMUNICATION_CHANNEL_TAB_TYPES: readonly ChannelTabType[] = ['all', ...COMMUNICATION_CHANNELS] as const;

export function isValidChannelTabType(channel: string): channel is ChannelTabType {
  return COMMUNICATION_CHANNEL_TAB_TYPES.includes(channel as ChannelTabType);
}

// based on communication_model from server/api/schemas/schemas.py

export interface Communication {
  id: number;
  claim_id: number;
  claim_id_display: string;
  channel: CommunicationChannelKey;
  direction: string;
  adjuster_id: string;
  adjuster: string;
  contact: ContactBaseModel | null;
  datetime: string;
  exposure_ids: number[];
  summary: string;
  extra: unknown;
  is_delivered: boolean;
  is_delivery_issue_error_exists: boolean;
  adjuster_recipient_type?: RecipientType;
  internal_email_address?: string;
  communication_ai_analysis: Record<string, unknown>;
  categories?: Array<Category>;
}

// based on email_communication_model from server/api/schemas/schemas.py
export interface EmailCommunication extends Communication {
  is_acknowledgment_email: boolean;
  channel: 'email';
}

// based on video_call_communication_base_model from server/api/schemas/schemas.py
export interface VideoCallCommunicationBase extends Communication {
  is_meeting_cancelled: boolean;
  is_meeting_expired: boolean;
  meeting_start_datetime: string;
  meeting_end_datetime: string;
  channel: 'video_call';
}

// based on sms_communication_base_model from server/api/schemas/schemas.py
export interface SmsCommunicationBase extends Communication {
  is_attachment_exist: boolean;
  delivery_status: string;
  delivery_error_reason: string;
  is_delivery_issue_error_exists: boolean;
  message: string;
  channel: 'sms';
}

// Phone call communication extra data
export interface PhoneCallExtraData {
  phone_number?: string;
}

// based on phone_call_communication_model from server/api/schemas/schemas.py
export interface PhoneCallCommunicationBase extends Communication {
  contact_phone: ContactPhone;
  content: string;
  specific_identifier: string;
  recording_stored_file_id: number;
  recording_status: string;
  additional_conference_participants: [PhoneCallConferenceParticipant];
  forwarded_phone_number: string;
  channel: 'phone_call';
  extra: PhoneCallExtraData;
}

// based on phone_call_conference_participant_model from server/api/schemas/schemas.py
interface PhoneCallConferenceParticipant {
  id: number;
  phone_call_communication_id: number;
  participant_call_sid: string;
  contact_id: number;
  contact: ContactBaseModel;
  contact_phone: ContactPhone;
  name: string;
  phone_number: string;
  status: string;
}

interface VideoChatMessage {
  display_name: string;
  created_at: string;
  type: 'text' | 'image' | 'file';
  text?: string;
  attached_document_filename?: string;
}

export type VideoChatMessages = VideoChatMessage[];

// based on video_call_communication_model from server/api/schemas/schemas.py
export interface VideoCallCommunication extends VideoCallCommunicationBase {
  provider: ProvidersUnion;
  recording_url: string;
  recording_status: string;
  transcript: string;
  chat?: VideoChatMessages;
  joined_participants: string[];
  attachments: CommunicationAttachment[];
  provider_specific_details: null | DyteProviderSpecificDetails;
}

export interface DyteProviderSpecificDetails {
  host_token: string;
  guest_link: string;
  dyte_meeting_id: string;
  background_images: { image_url: string; stored_file_id: string }[];
}

// based on communication_attachment_model from server/api/schemas/schemas.py
export interface CommunicationAttachment {
  id: number;
  communication_id: number;
  stored_file: StoredFileMetadata | null;
  datetime_updated: string;
  document: DocumentMetadata | null;
  file_name: string;
  is_blocked: boolean;
}

// based on stored_file_metadata_model from server/api/schemas/schemas.py
interface StoredFileMetadata {
  id: number;
  datetime_uploaded: string;
  mime_type: string;
  size: number;
  original_filename: string;
  stored_file_extra: unknown;
}

export interface DocumentExtra {
  created_by_ai?: boolean;
  [key: string]: unknown;
}

export interface CliveStoreInformation {
  processing_status?: string; // Values: 'processing' | 'error' | 'success'
  short_description?: string;
  full_summary?: string;
}

// based on document_metadata_model from server/api/schemas/schemas.py
export interface DocumentMetadata {
  id: number;
  api_uid: string;
  claim_internal_id: number;
  claim_id: number;
  exposure_ids: number[];
  datetime_uploaded: string;
  document_name: string;
  document_name_with_ext: string;
  document_date: string;
  were_photos_extracted: boolean;
  summary: string;
  type: string;
  mime_type: string;
  size: number;
  datetime_updated: string;
  is_removed: boolean;
  removed_date: string | null;
  removed_reason: string;
  uploaded_by: string;
  document_extra: DocumentExtra;
  stored_file: StoredFileMetadata | null;
  date_received: string;
  document_date_received: string;
  clive_store_information?: CliveStoreInformation;
  configured_fields_values?: Record<string, unknown>;
}

export type VideoCallSpecificBodyCommunicationState = 'ready' | 'ongoing' | 'expired' | 'canceled' | 'ended';

// based on internal_communication_model from server/api/schemas/schemas.py
export interface InternalCommunication {
  id: number;
  type: string;
  title: string;
  details: string;
  claim_id: number;
  claim_id_display: string;
  recipient_id: string;
  recipient: string;
  sender_id: string;
  sender: string;
  exposure_ids: number[];
  creation_datetime: string;
}

export type CommunicationType =
  | EmailCommunication
  | VideoCallCommunicationBase
  | SmsCommunicationBase
  | PhoneCallCommunicationBase
  | PhoneCallCommunication;

export interface Recommendation {
  title: string;
  explanation_component: React.ReactNode;
  classification_reasons: ClassificationReason[];
  actionComponent: React.ReactNode;
}

interface SourceQuote {
  location_desc: string;
  quote: string;
}

export interface ClassificationReason {
  reason: string;
  quotes: SourceQuote[] | null;
}

/**
 * Type guard to check if a communication is EmailCommunicationBase
 * @param communication - The communication to check
 * @returns true if communication is EmailCommunicationBase, false otherwise
 */
export function isEmailCommunication(
  communication: DataContractsCommunication
): communication is DataContractsEmailCommunicationBase {
  return communication !== null && communication !== undefined && communication.channel === 'email';
}
